/**
 * The stored config uses a deliberately tiny HTML vocabulary: `<p>`, `<ul>` and
 * `<li>`, with no attributes anywhere. Editors emit far more than that (wrapper
 * divs, `<br>`, `&nbsp;`, inline styles, `<li><p>text</p></li>`), so everything
 * the editor produces is normalised back down to that vocabulary before it is
 * put in state. Anything outside the allow-list is unwrapped, not dropped, so
 * text is never silently lost.
 */
const ALLOWED_TAGS = new Set(['P', 'UL', 'LI', 'STRONG', 'EM', 'A']);

/** Tags whose text content is discarded rather than unwrapped. */
const DROPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME']);

const TAG_ALIASES: Record<string, string> = {
  B: 'STRONG',
  I: 'EM',
  DIV: 'P'
};

const escapeText = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeAttribute = (value: string): string => escapeText(value).replace(/"/g, '&quot;');

/** The link schemes the normaliser will keep; anything else is unwrapped to plain text. */
export const isSafeHref = (href: string): boolean => /^(https?:|mailto:)/i.test(href.trim());

const serialize = (node: Node, insideListItem: boolean): string => {
  if (node.nodeType === 3) {
    // Non-breaking spaces come from contentEditable padding, not from the author.
    return escapeText((node.textContent ?? '').replace(/\u00a0/g, ' '));
  }

  if (node.nodeType !== 1) {
    return '';
  }

  const element = node as Element;
  const rawTag = element.tagName.toUpperCase();
  const tag = TAG_ALIASES[rawTag] ?? rawTag;

  if (rawTag === 'BR') {
    return ' ';
  }

  if (DROPPED_TAGS.has(rawTag)) {
    return '';
  }

  const isListItem = tag === 'LI';
  const children = Array.from(element.childNodes)
    .map(child => serialize(child, insideListItem || isListItem))
    .join('');

  // `<li><p>text</p></li>` is an editor artefact; the stored form is `<li>text</li>`.
  if (tag === 'P' && insideListItem) {
    return children;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    return children;
  }

  if (!children.trim() && tag !== 'A') {
    return '';
  }

  if (tag === 'A') {
    const href = element.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      return children;
    }
    return `<a href="${escapeAttribute(href)}">${children}</a>`;
  }

  return `<${tag.toLowerCase()}>${children}</${tag.toLowerCase()}>`;
};

export const normalizeContentHtml = (html: string): string => {
  if (!html || !html.trim()) {
    return '';
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const result = Array.from(container.childNodes)
    .map(node => serialize(node, false))
    .join('')
    // Collapse the whitespace editors leave between block tags.
    .replace(/>\s+</g, '><')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

  // Bare text with no block wrapper is stored as a paragraph, matching the file.
  if (result && !/^</.test(result)) {
    return `<p>${result}</p>`;
  }

  return result;
};
